import { Icon } from "semantic-ui-react";
import { useLocation, useParams } from "react-router";
import DetailsHeaderTitle from "./DetailsHeaderTitle";
import SectionHeader from "../SectionHeader";
import EllipsisPopupButton from "../../buttons/ellipsisPopupButton/EllipsisPopupButton";
import { ButtonWithIcon } from "../../buttons/buttonWithIcon";
import { type PublishedStatusType, PublishedStatusTypes } from "../../../enums";
import RestrictedByTooltip from "../../restrictedByTooltip/RestrictedByTooltip";
import { Tooltip } from "../../common/tooltip";
import { toggleEntityStateButtonsVisibility } from "utils/toggleEntityStateButtonsVisibility";
import { ReportLastUpdated } from "features/Reporting/Content/shared/ReportLastUpdated";

import "./outstandingDetailsHeader.scss";
import { type PropsWithChildren, type ReactElement, type ReactNode } from "react";
import noop from "lodash/noop";

export type Props = PropsWithChildren<{
  title?: ReactElement | string;
  pageTitle?: string;
  titleForGA?: string;
  publishedStatus?: PublishedStatusType;
  isRevertVisible?: boolean;
  isRevertDisabled?: boolean;
  canBePublished?: boolean;
  canBeEdited?: boolean;
  invalidFormDetails?: boolean;
  actionButtons?: ReactNode;
  entityStateActions?: {
    onPublish?: () => void;
    onRevert?: () => void;
    onClose: () => void;
    onEdit: () => void;
  };
  className?: string;
  style?: object;
  isReadOnly?: boolean;
  showReadOnlyLabel?: boolean;
  hideEntityStateButtons?: boolean;
  crudPermission?: boolean;
  isEllipsisMenuDisabled?: boolean;
  renderEditButton?: boolean;
  publishTooltipMessage?: string;
  backButton?: boolean | string | (() => string | void | undefined);
  defaultURL?: string;
  showReportLastUpdated?: boolean;
}>;

export function OutstandingDetailsHeader(props: Props) {
  const {
    actionButtons,
    entityStateActions,
    children,
    style,
    publishedStatus = "",
    isRevertVisible,
    canBePublished,
    canBeEdited = false,
    invalidFormDetails,
    isRevertDisabled,
    hideEntityStateButtons = false,
    isEllipsisMenuDisabled,
    crudPermission = true,
    renderEditButton,
    publishTooltipMessage,
    isReadOnly = false,
    showReadOnlyLabel,
    showReportLastUpdated = false,
    ...headerTitleProps
  } = props;

  const { id = "" } = useParams();
  const location = useLocation();

  const renderPublishedStateButtons = () => {
    return (
      <RestrictedByTooltip hasPermission={crudPermission}>
        <ButtonWithIcon
          id="edit-details"
          key="edit-details"
          className="details-handle-buttons"
          basic
          iconName="edit"
          label="EDIT"
          onClick={entityStateActions?.onEdit || noop}
          disabled={!crudPermission || !canBeEdited}
        />
      </RestrictedByTooltip>
    );
  };

  const renderReadOnlySection = () => (
    <div className="read-only-label">
      <Icon className="fas fa-ban icon" size="large" />
      READ ONLY
    </div>
  );

  const renderEmptyReadOnlySection = () => <div />;

  const renderDraftStateButtons = () => {
    return (
      <>
        <RestrictedByTooltip hasPermission={crudPermission}>
          <Tooltip
            showAlways={!!publishTooltipMessage}
            inverted
            hideOnScroll
            position="left center"
            content={publishTooltipMessage}
            target={
              <ButtonWithIcon
                id="publish-details"
                key="publish-details"
                className="details-handle-buttons"
                primary
                iconName="eye"
                label="PUBLISH"
                onClick={entityStateActions?.onPublish || noop}
                disabled={!crudPermission || invalidFormDetails || canBePublished === false}
              />
            }
          />
        </RestrictedByTooltip>
        {isRevertVisible && (
          <RestrictedByTooltip hasPermission={crudPermission}>
            <ButtonWithIcon
              id="revert-details"
              key="revert-button"
              className="details-handle-buttons"
              basic
              iconName="undo"
              label="REVERT"
              onClick={entityStateActions?.onRevert || noop}
              disabled={!crudPermission || isRevertDisabled}
            />
          </RestrictedByTooltip>
        )}
        <ButtonWithIcon
          id="close-details"
          key="close-details"
          className="details-handle-buttons"
          basic
          iconName="times"
          label="CLOSE"
          onClick={entityStateActions?.onClose || noop}
        />
      </>
    );
  };

  const renderEntityStateButtons = () => {
    if (isReadOnly) {
      return showReadOnlyLabel ? renderReadOnlySection() : renderEmptyReadOnlySection();
    }
    if (canBeEdited && (renderEditButton || publishedStatus === PublishedStatusTypes.published)) {
      return renderPublishedStateButtons();
    }

    if (publishedStatus === PublishedStatusTypes.draft || publishedStatus === PublishedStatusTypes.notPublished) {
      return renderDraftStateButtons();
    }

    return null;
  };
  return (
    <SectionHeader
      header={<DetailsHeaderTitle {...headerTitleProps} />}
      publishedStatus={publishedStatus}
      className="details-header"
      style={style}
    >
      <div className="top-row">
        {children}
        {showReportLastUpdated && (
          <div className="reportLastUpdated">
            <ReportLastUpdated />
          </div>
        )}
        {!hideEntityStateButtons &&
          toggleEntityStateButtonsVisibility(id, location.pathname) &&
          renderEntityStateButtons()}
        {actionButtons && (
          <RestrictedByTooltip hasPermission={crudPermission}>
            <EllipsisPopupButton disabled={!crudPermission || isEllipsisMenuDisabled}>
              {actionButtons}
            </EllipsisPopupButton>
          </RestrictedByTooltip>
        )}
      </div>
    </SectionHeader>
  );
}

OutstandingDetailsHeader.defaultProps = {
  title: "",
  isRevertDisabled: false,
  canBeEdited: false,
  isReadOnly: false,
  showReadOnlyLabel: true,
  hideEntityStateButtons: false,
  crudPermission: true,
};

export default OutstandingDetailsHeader;
